import { useAnalytics } from '@analytics';
import { ContactFields, FormContactFieldsType } from '@brighte/passwordless-authenticator';
import { ApplicantType, CodeAcceptanceStatus, contactDetailsSchema } from '@brighte/validation';
import { StepNavigator } from '@components/StepNavigator';
import { useHandlePostApplicationLoginMutation } from '@graphql/index';
import { useApplicationData, useSetApplicationData } from '@provider';
import { APPLICATION_STEPS, CONTACT_DETAILS, getTrackingId, isJointApplicant } from '@utils';
import { useContactDetailsSummary } from './useContactDetailsSummary';

const ContactDetails = () => {
  const { TITLE } = CONTACT_DETAILS;
  const contactDetailsSummary = useContactDetailsSummary();
  const prePopContactDetails = useApplicationData('ContactDetails');
  const { quote } = useApplicationData(['quote']);
  const setApplicationData = useSetApplicationData();
  const { trackAndIdentifySignIn } = useAnalytics();
  const { mutate } = useHandlePostApplicationLoginMutation();
  const handleVerificationSuccess = ({
    verifiedEmail,
    verifiedMobile,
  }: {
    verifiedEmail?: string;
    verifiedMobile: string;
  }) => {
    setApplicationData({ ContactDetails: { email: verifiedEmail, mobile: verifiedMobile } });
    mutate({
      postLoginInput: {
        applicantType: isJointApplicant() ? ApplicantType.Joint : ApplicantType.Primary,
        opportunityId: quote?.opportunity?.id,
        trackingId: getTrackingId(),
      },
    });
  };

  const handleError = (error: unknown) => {
    throw error;
  };

  return (
    <StepNavigator<FormContactFieldsType>
      schema={contactDetailsSchema}
      name={APPLICATION_STEPS.CONTACT_DETAILS}
      title={TITLE}
      defaultValues={{
        ...prePopContactDetails,
        newEmail: prePopContactDetails?.email,
        newMobile: prePopContactDetails?.mobile,
        otp: undefined,
        otpSent: false,
        codeAccepted: CodeAcceptanceStatus.NotAccepted,
        updateOtpSent: false,
      }}
      isLoading={false}
      isEditable={false}
      completedContent={contactDetailsSummary}
      hideSubmitButton={true}
    >
      {() => {
        if (!prePopContactDetails) {
          return null;
        }

        return (
          <ContactFields
            verificationDetails={{
              email: prePopContactDetails.email,
              mobile: prePopContactDetails.mobile,
              verificationType: prePopContactDetails.verificationType,
              maskedEmail: prePopContactDetails.maskedEmail,
              maskedMobile: prePopContactDetails.maskedMobile,
            }}
            onTracking={trackAndIdentifySignIn}
            onError={handleError}
            onSuccess={handleVerificationSuccess}
            financialProductId={quote?.financialProduct?.id}
          />
        );
      }}
    </StepNavigator>
  );
};

export { ContactDetails };
